import api from './api';

/**
 * PO-SHEET.1 — the order sheet's wire shape.
 *
 * `orderQty` is typed `null` and nothing else, deliberately. The server never
 * sends a quantity, so the client cannot inherit one: the law is a type here,
 * not a convention somebody has to remember.
 */

export type SuggestionBasis = 'PAR_FILL' | 'ROP_DOUBLE' | 'DAYS_OF_COVER' | 'NONE';

export interface OrderSheetSuggestion {
  qty: number | null;
  basis: SuggestionBasis;
  /** The arithmetic, rendered verbatim: "par 12 − 3 = 9". */
  explain: string;
  confidence: 'EXACT' | 'ESTIMATED';
  /** The stored count was negative; clamped to zero and flagged. */
  needsCount: boolean;
}

export interface OrderSheetVelocity {
  unitsPerDay: number;
  daysOfCover: number | null;
  sampleDays: number;
  basedOnMovements: number;
}

export interface OrderSheetLine {
  inventoryItemId: string;
  displayName: string | null;
  sizeValue: number | null;
  sizeUnit: string | null;
  shortId: string | null;
  onHand: number;
  parLevel: number | null;
  reorderPoint: number | null;
  suggestion: OrderSheetSuggestion;
  velocity: OrderSheetVelocity | null;
  lastCost: number | null;
  costSource: 'SUPPLIER_LINK' | 'BALANCE' | 'NONE';
  unlinked: boolean;
  onOpenOrder: { purchaseOrderId: string; orderedQty: number } | null;
  /** Always null on the wire. See the law. */
  orderQty: null;
}

export interface OrderSheetResponse {
  supplier: { id: string; name: string | null };
  currency: string | null;
  lines: OrderSheetLine[];
  meta: {
    totalLines: number;
    truncated: boolean;
    unlinkedFallback: boolean;
    parCoverage: number;
    costCoverage: number;
    velocityCoverage: number;
  };
}

const base = (venueId: string) => `/admin/venue/${venueId}/purchase-order/order-sheet`;

const orderSheetService = {
  get: async (
    venueId: string,
    supplierId: string,
    opts: { q?: string; itemIds?: string[] } = {},
  ): Promise<OrderSheetResponse> => {
    const params: Record<string, string> = { supplierId };
    if (opts.q) params.q = opts.q;
    if (opts.itemIds?.length) params.itemIds = opts.itemIds.join(',');
    const res = await api.get<OrderSheetResponse>(base(venueId), { params });
    return res.data;
  },
};

export default orderSheetService;
